import React, { useMemo, useCallback } from 'react';
import { Box, Paper, IconButton, Button } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx } from '../../theme/tokens';
import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm4_u2_x3_voc';

const images = [
  { key: 'placeholder', src: '/assets/drawable/zzz_help.png', label: 'VOC Illustration' }
];

const textBlocks = [
    { key: 'block_titel', heading: 'Mischgase / VOC verstehen', body: '' },
    { key: 'block_was', heading: '1. Was sind VOCs (Mischgase)?', body: 'VOC steht für "Volatile Organic Compounds" – flüchtige organische Verbindungen. Große Stofffamilie, leicht verdampfend bei Raumtemperatur. Viele treten gemeinsam auf → TVOC als Summenwert.' },
    { key: 'block_quellen', heading: '2. Woher kommen diese VOCs?', body: 'Quellen: Baumaterialien, Möbel, Farben, Lacke, Klebstoffe, Reinigungsmittel, Kosmetika, Drucker, Rauchen, Kochen, Kerzen, Bastelaktivitäten, neue Textilien, menschliche Ausdünstungen.' },
    { key: 'block_luftqualitaet', heading: '3. VOCs und die Luftqualität in Räumen', body: 'Innen höhere Konzentrationen als außen durch begrenzten Luftaustausch – Anreicherung möglich. Schlechtere Luftqualität, Gerüche, mögliche Reizstoffe.' },
    { key: 'block_auswirkungen', heading: '4. Wie wirken sich VOCs auf uns aus?', body: 'Kurzfristig: Reizung von Augen/Nase/Rachen, Kopfschmerzen, Schwindel, Müdigkeit, Gerüche.\nLangfristig: Teils krebserregend (Formaldehyd, Benzol), Organschäden möglich. Wirkung abhängig von Art, Konzentration, Expositionsdauer, Sensibilität.' },
    { key: 'block_beispiele', heading: '5. Einige bekannte VOC-Beispiele', body: 'Formaldehyd (Spanplatten, Kleber), Benzol (Tabakrauch), Toluol/Xylol (Farben/Lacke), Terpene (Duftstoffe, Holz). Reaktionen mit Ozon → Sekundärstoffe.' },
    { key: 'block_messung', heading: '6. Wie misst man VOCs? Der TVOC-Wert', body: 'TVOC = Gesamtmenge erfasster VOCs. Metalloxid-Sensoren, ppb/µg/m³ Werte. Gute Trend-/Belastungsanzeige ohne Einzelstoffanalyse.' },
    { key: 'block_richtwerte', heading: '7. Richtwerte für TVOC – Wann wird es kritisch?', body: 'Unter 300 µg/m³: Sehr gut.\n300–1000: OK.\n1000–3000: Auffällig → Lüften.\n3000–10000: Bedenklich → Quellen suchen.\n>10000: Nicht akzeptabel → Sofortige Maßnahmen. Werte sind Orientierungen.' },
    { key: 'block_massnahmen', heading: '8. Was tun gegen zu viele VOCs?', body: 'Lüften (Stoßlüftung). Bedarfsgerechte Lüftung mit Sensoren. Emissionsarme Produkte wählen (Siegel). Quellen auslüften lassen. Weniger Duft-/Chemieprodukte. Nicht rauchen. Aktivkohlefilter/Luftreiniger ergänzend.' },
    { key: 'block_kurz', heading: '9. Kurz zusammengefasst', body: 'VOCs belasten Raumluft und Gesundheit. TVOC dient als Gesamtindikator. Maßnahmen: Lüften, emissionsarme Materialien, sensorgesteuerte Lüftung, Quellen minimieren.' }
];

const M4_U2_X3_Voc: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM4, canUploadImagesM4 } = useAuth();
  const canEditTexts = canEditTextsM4;
  const canUploadImages = canUploadImagesM4;
  const { darkMode } = useTheme();
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const mergedBlocks = useMemo(() => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })), [override]);
  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try { await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token); alert('Gespeichert.'); await refresh(); } catch (e) { console.error(e); alert('Fehler.'); }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try { await uploadUnitAsset(PAGE_ID, imageKey, file, token); alert('Bild aktualisiert.'); await refresh(); } catch (e) { console.error(e); alert('Fehler.'); }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'TTS', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '4.2.3 VOC – Mischgase & Luftqualität', actions: ttsBtn },
    React.createElement(Paper, { sx: getCardSx(darkMode), elevation: 2 },
      React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, images.map(img => React.createElement(ZoomableImage, { key: img.key, src: imgSrc(img.key, img.src), label: img.label, onReplace: canUploadImages ? (f: File) => handleImageReplace(img.key, f) : undefined })) ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, mergedBlocks.map(tb => React.createElement(EditableTextBlock, { key: tb.key, heading: tb.heading, body: tb.body, onSave: canEditTexts ? (v: string) => handleTextSave(tb.key, v) : undefined, pageId: PAGE_ID, blockId: tb.key })) )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m4_u2_x99_quiz') }, 'Zum Quiz')
    )
  );
};

export default M4_U2_X3_Voc;

